import React, { useEffect, useRef, useState } from "react";
import Box from "@material-ui/core/Box";
import { Icon, SecondaryButton } from "@prestosports/commons-ui";
import Reset32 from "@carbon/icons-react/lib/reset/32";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { useEventContext } from "../../../state/events/context";
import Configuration from "./Configuration";
import commonStyles from "../styles/Common.styles";
import RestoreSettings from "./RestoreSettings";
import ValidationErrors from "./ValidationErrors";
import { useGameContextContext } from "../../../state/game-context/context";
import { useAppContext } from "../../../state/app/context";
import eventSettingsStyles from "../styles/EventSettings.styles";
import RuleSetSelect from "./RuleSetSelect";
import { useSportContext } from "../../../state/sport/context";
import SettingConfirmationDialog from "./SettingConfirmationDialog";
import RuleSetConfirmationDialog from "./RuleSetConfirmationDialog";
import { getSportConfiguration } from "../config/sport-configurations";

const EventSettings = () => {
	const xsScreen = useMediaQuery((theme) => theme.breakpoints.down("xs"));

	const {
		state: sportState,
		getRuleSetOptions,
		getCustomValidation
	} = useSportContext();
	const ruleSetOptions = sportState.ruleSetOptions();
	const customValidation = getCustomValidation();

	const {
		state: appState,
		setConfirmSettingSave,
		resetConfirmSettingSave
	} = useAppContext();
	const allowEventSettingsEditing = appState.allowEventSettingsEditing();
	const confirmSaveOpen = appState.confirmSettingSave();
	const settingChangeSummary = appState.settingChangeSummary();

	const { state: gameContextState } = useGameContextContext();
	const ruleSetId = gameContextState.ruleSetId();
	const isGameInitialized = gameContextState.isGameInitialized();
	const eventSettingsMessages = gameContextState.eventSettingsMessages();

	const commonClasses = commonStyles({
		withAlert: !!eventSettingsMessages.length,
		withHeader: false
	});
	const classes = eventSettingsStyles();

	const {
		state,
		saveGameConfiguration,
		restoreDefaultGameConfiguration,
		setRuleSetId
	} = useEventContext();
	const { gameConfiguration, customFormData } = state.currentEventSettings();
	const validationErrors = state.validationErrors();
	const { sportId } = state.currentEvent() || {};

	const [restoreConfirmationOpen, setRestoreConfirmationOpen] = useState(false);
	const [confirmRulesOpen, setConfirmRulesOpen] = useState(false);
	const [pendingRuleSet, setPendingRuleSet] = useState(null);
	const [pendingConfig, setPendingConfig] = useState(null);

	const resetFormRef = useRef();

	const confirmLoadRules = () => {
		setRuleSetId(pendingRuleSet);
		setConfirmRulesOpen(false);
	};

	const cancelLoadRules = () => {
		setConfirmRulesOpen(false);
	};

	const onRestoreDefaults = () => {
		setRestoreConfirmationOpen(true);
	};

	const confirmRestore = () => {
		restoreDefaultGameConfiguration();
		setRestoreConfirmationOpen(false);
		resetFormRef.current.reset();
	};

	const cancelRestore = () => {
		setRestoreConfirmationOpen(false);
	};

	const handleConfirmSave = (updatedConfig) => {
		setPendingConfig(updatedConfig);
		setConfirmSettingSave(updatedConfig);
	};

	const handleSave = () => {
		saveGameConfiguration(pendingConfig);
		resetConfirmSettingSave();
	};

	const canEdit = () => {
		return !isGameInitialized || allowEventSettingsEditing;
	};

	useEffect(() => {
		getRuleSetOptions();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<>
			<Box className={commonClasses.container}>
				{!!eventSettingsMessages.length && (
					<Box className={commonClasses.headerContainer}>
						<ValidationErrors
							errors={[...eventSettingsMessages, ...validationErrors]}
						/>
					</Box>
				)}
				<Box className={commonClasses.contentContainer}>
					<Configuration
						ref={resetFormRef}
						configuration={gameConfiguration}
						customFormData={customFormData}
						onSaveConfig={handleConfirmSave}
						disabled={!canEdit()}
						customValidation={customValidation}
						extension={getSportConfiguration(sportId)}
						secondaryActions={
							canEdit() && (
								<Box className={classes.secondaryActionContainer}>
									<SecondaryButton
										label={xsScreen ? "Defaults" : "Restore defaults"}
										startIcon={
											<Icon>
												<Reset32 />
											</Icon>
										}
										onClick={onRestoreDefaults}
									/>
									<RuleSetSelect
										ruleSetOptions={ruleSetOptions}
										ruleSetId={ruleSetId}
										onRuleSelect={(e) => {
											setPendingRuleSet(e.target.value);
											setConfirmRulesOpen(true);
										}}
									/>
								</Box>
							)
						}
					/>
				</Box>
			</Box>

			<SettingConfirmationDialog
				isOpen={confirmSaveOpen}
				onClose={resetConfirmSettingSave}
				onSave={handleSave}
				changeSummary={settingChangeSummary}
			/>

			<RuleSetConfirmationDialog
				restoreConfirmationOpen={confirmRulesOpen}
				confirmRestore={confirmLoadRules}
				cancelRestore={cancelLoadRules}
				ruleSetName={ruleSetOptions.find((o) => o.id === pendingRuleSet)?.name}
			/>

			<RestoreSettings
				restoreConfirmationOpen={restoreConfirmationOpen}
				confirmRestore={confirmRestore}
				cancelRestore={cancelRestore}
			/>
		</>
	);
};

export default EventSettings;
